import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../../css/BoardMembers.css';

const BoardMembers = ({ boardId, userRole, onClose, onMembersChanged }) => {
    const { apiCall, user } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('EDITOR');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const canManageMembers = userRole === 'OWNER' || userRole === 'ADMIN';

    const fetchMembers = useCallback(async () => {
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/members/`);
            if (response.ok) {
                const data = await response.json();
                setMembers(data);
            }
        } catch (err) {
            console.error('Error fetching members:', err);
        }
        setLoading(false);
    }, [apiCall, boardId]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const handleAddMember = async () => {
        if (!email.trim()) return;

        setAdding(true);
        setError('');
        setSuccess('');

        try {
            const response = await apiCall(`/tasks/boards/${boardId}/members/add/`, {
                method: 'POST',
                body: JSON.stringify({ email: email.trim(), role }),
            });

            const data = await response.json();

            if (response.ok) {
                setSuccess(`${email.trim()} added as ${role.toLowerCase()}`);
                setEmail('');
                setRole('EDITOR');
                await fetchMembers();
                if (onMembersChanged) onMembersChanged();
            } else {
                setError(data.error || 'Failed to add member');
            }
        } catch (err) {
            setError('Failed to add member');
        }
        setAdding(false);
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/members/${userId}/update/`, {
                method: 'PUT',
                body: JSON.stringify({ role: newRole }),
            });

            if (response.ok) {
                await fetchMembers();
                if (onMembersChanged) onMembersChanged();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update role');
            }
        } catch (err) {
            setError('Failed to update role');
        }
    };

    const handleRemoveMember = async (userId, memberName) => {
        if (!window.confirm(`Remove ${memberName} from this board?`)) return;

        try {
            const response = await apiCall(`/tasks/boards/${boardId}/members/${userId}/remove/`, {
                method: 'DELETE',
            });

            if (response.ok) {
                await fetchMembers();
                if (onMembersChanged) onMembersChanged();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to remove member');
            }
        } catch (err) {
            setError('Failed to remove member');
        }
    };

    const handleLeaveBoard = async () => {
        if (!window.confirm('Are you sure you want to leave this board?')) return;

        try {
            const response = await apiCall(`/tasks/boards/${boardId}/leave/`, {
                method: 'POST',
            });

            if (response.ok) {
                // Trigger navigation back to board list
                if (onMembersChanged) onMembersChanged();
                onClose();
                window.location.href = '/';
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to leave board');
            }
        } catch (err) {
            setError('Failed to leave board');
        }
    };

    const getInitials = (name, email) => {
        if (name && name.trim()) {
            return name.split(' ').map(w => w[0]).join('').slice(0, 2);
        }
        return email ? email[0] : '?';
    };

    const canEditMember = (memberRole) => {
        if (userRole === 'OWNER') return memberRole !== 'OWNER';
        if (userRole === 'ADMIN') return memberRole !== 'OWNER' && memberRole !== 'ADMIN';
        return false;
    };

    return (
        <div className="BoardMembers__overlay" onClick={onClose}>
            <div className="BoardMembers" onClick={(e) => e.stopPropagation()}>
                <div className="BoardMembers__header">
                    <h3 className="BoardMembers__title">Board Members</h3>
                    <button className="BoardMembers__close" onClick={onClose}>×</button>
                </div>

                {canManageMembers && (
                    <div className="BoardMembers__add">
                        <label className="BoardMembers__add-label">Add Member</label>
                        <div className="BoardMembers__add-row">
                            <input
                                className="BoardMembers__add-email"
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setError(''); setSuccess(''); }}
                                placeholder="Email address..."
                                onKeyDown={(e) => e.key === 'Enter' && handleAddMember()}
                            />
                            <select
                                className="BoardMembers__add-role"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                            >
                                {userRole === 'OWNER' && <option value="ADMIN">Admin</option>}
                                <option value="EDITOR">Editor</option>
                                <option value="VIEWER">Viewer</option>
                            </select>
                            <button
                                className="BoardMembers__add-btn"
                                onClick={handleAddMember}
                                disabled={adding || !email.trim()}
                            >
                                {adding ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                        {error && <div className="BoardMembers__add-error">{error}</div>}
                        {success && <div className="BoardMembers__add-success">{success}</div>}
                    </div>
                )}

                <div className="BoardMembers__list">
                    <div className="BoardMembers__list-title">
                        Members ({members.length})
                    </div>

                    {loading ? (
                        <div className="BoardMembers__empty">Loading...</div>
                    ) : members.length === 0 ? (
                        <div className="BoardMembers__empty">No members found</div>
                    ) : (
                        members.map((member) => (
                            <div key={member.user_id} className="BoardMembers__member">
                                <div className="BoardMembers__avatar">
                                    {getInitials(member.name, member.email)}
                                </div>
                                <div className="BoardMembers__info">
                                    <div className="BoardMembers__name">
                                        {member.name || member.email}
                                        {user && member.email === user.email && (
                                            <span className="BoardMembers__you-badge">you</span>
                                        )}
                                    </div>
                                    <div className="BoardMembers__email">{member.email}</div>
                                </div>

                                {canEditMember(member.role) ? (
                                    <>
                                        <select
                                            className="BoardMembers__role-select"
                                            value={member.role}
                                            onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                                        >
                                            {userRole === 'OWNER' && <option value="ADMIN">Admin</option>}
                                            <option value="EDITOR">Editor</option>
                                            <option value="VIEWER">Viewer</option>
                                        </select>
                                        <button
                                            className="BoardMembers__remove-btn"
                                            onClick={() => handleRemoveMember(member.user_id, member.name || member.email)}
                                            title="Remove member"
                                        >
                                            ×
                                        </button>
                                    </>
                                ) : (
                                    <span className={`BoardMembers__role-badge BoardMembers__role-badge--${member.role}`}>
                                        {member.role}
                                    </span>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {userRole !== 'OWNER' && (
                    <div className="BoardMembers__footer">
                        <button className="BoardMembers__leave-btn" onClick={handleLeaveBoard}>
                            Leave Board
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BoardMembers;
